import { put, del, list, head } from '@vercel/blob';

class VercelBlobService {
  constructor() {
    this.baseUrl = 'https://gifted-solutions-ecommerce.vercel.app';
    this.bucketName = 'products';
    this.storeId = 'store_Tz896OQOYcnpeXq2';
    this.storePath = 'gifted-solutions-images';
    console.log(`🗄️ VercelBlobService initialized for store: ${this.storeId}`);
  }

  /**
   * Upload a file to Vercel Blob storage
   * @param {File} file - The file to upload
   * @param {string} filename - The desired filename
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadFile(file, filename, options = {}) {
    try {
      console.log(`📤 Uploading ${filename} to Vercel Blob...`);

      const blob = await put(filename, file, {
        access: 'public',
        addRandomSuffix: false,
        ...options
      });

      console.log(`✅ Successfully uploaded ${filename} to Vercel Blob`);

      return {
        success: true,
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        filename: filename
      };
    } catch (error) {
      console.error('Error uploading file to Vercel Blob:', error);
      return {
        success: false,
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * Upload a text file to Vercel Blob storage
   * @param {string} filename - The desired filename
   * @param {string} content - The text content
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadText(filename, content, options = {}) {
    try {
      console.log(`📝 Uploading text file ${filename} to Vercel Blob...`);

      const blob = await put(filename, content, {
        access: 'public',
        addRandomSuffix: false,
        ...options
      });

      console.log(`✅ Successfully uploaded text file ${filename} to Vercel Blob`);

      return {
        success: true,
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        filename: filename,
        type: 'text'
      };
    } catch (error) {
      console.error('Error uploading text file to Vercel Blob:', error);
      return {
        success: false,
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * Upload a product image with automatic category detection
   * @param {File} file - The image file to upload
   * @param {string} category - Product category (optional)
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadProductImage(file, category = null, metadata = {}) {
    try {
      // Generate filename with category prefix if provided
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      const categoryPrefix = category ? `${category.toLowerCase()}-` : '';
      const filename = `products/${categoryPrefix}${baseName}-${timestamp}.${fileExtension}`;

      console.log(`🖼️ Uploading product image ${filename} to Vercel Blob...`);

      const blob = await put(filename, file, {
        access: 'public',
        addRandomSuffix: false
      });

      console.log(`✅ Successfully uploaded product image ${filename} to Vercel Blob`);

      // Also save metadata to Firebase if provided
      if (metadata && Object.keys(metadata).length > 0) {
        try {
          const { default: firebaseMetadataService } = await import('./firebaseMetadataService');
          await firebaseMetadataService.saveImageMetadata(baseName + '.' + fileExtension, {
            ...metadata,
            category: category,
            vercel_blob_url: blob.url,
            vercel_blob_pathname: blob.pathname,
            storage_provider: 'vercel-blob'
          });
          console.log(`💾 Metadata saved to Firebase for ${filename}`);
        } catch (metadataError) {
          console.warn('Failed to save metadata to Firebase:', metadataError);
        }
      }

      return {
        success: true,
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        filename: filename,
        category: category,
        metadata: metadata,
        type: 'product-image'
      };
    } catch (error) {
      console.error('Error uploading product image to Vercel Blob:', error);
      return {
        success: false,
        error: error.message,
        filename: file.name
      };
    }
  }

  /**
   * Upload multiple files
   * @param {Array} files - Array of {file, filename} objects
   * @returns {Promise<Array>} Array of upload results
   */
  async uploadMultipleFiles(files) {
    const uploadPromises = files.map(({ file, filename, options }) =>
      this.uploadFile(file, filename, options)
    );

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      throw error;
    }
  }

  /**
   * Delete a file from Vercel Blob storage
   * @param {string} url - The blob URL to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFile(url) {
    try {
      await del(url);
      return {
        success: true,
        message: 'File deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting file from Vercel Blob:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * List all files in the blob storage
   * @param {Object} options - List options
   * @returns {Promise<Array>} Array of blob objects
   */
  async listFiles(options = {}) {
    try {
      const { blobs } = await list({
        prefix: options.prefix || '',
        limit: options.limit || 1000,
        cursor: options.cursor
      });

      return {
        success: true,
        files: blobs.map(blob => ({
          url: blob.url,
          downloadUrl: blob.downloadUrl,
          pathname: blob.pathname,
          size: blob.size,
          uploadedAt: blob.uploadedAt
        })),
        hasMore: blobs.length === (options.limit || 1000)
      };
    } catch (error) {
      console.error('Error listing files from Vercel Blob:', error);
      return {
        success: false,
        error: error.message,
        files: []
      };
    }
  }

  /**
   * Get file metadata
   * @param {string} url - The blob URL
   * @returns {Promise<Object>} File metadata
   */
  async getFileMetadata(url) {
    try {
      const metadata = await head(url);
      return {
        success: true,
        metadata: {
          url: metadata.url,
          downloadUrl: metadata.downloadUrl,
          pathname: metadata.pathname,
          size: metadata.size,
          uploadedAt: metadata.uploadedAt,
          contentType: metadata.contentType,
          cacheControl: metadata.cacheControl
        }
      };
    } catch (error) {
      console.error('Error getting file metadata:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate a public URL for a file
   * @param {string} filename - The filename
   * @returns {string} Public URL
   */
  generatePublicUrl(filename) {
    // For Vercel Blob, the URL is returned from the upload
    // This is a helper for constructing URLs if needed
    return `${this.baseUrl}/_vercel/blob/${filename}`;
  }

  /**
   * Check if a file exists
   * @param {string} url - The blob URL
   * @returns {Promise<boolean>} Whether the file exists
   */
  async fileExists(url) {
    try {
      const result = await this.getFileMetadata(url);
      return result.success;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all product images
   * @returns {Promise<Array>} Array of product image URLs
   */
  async getProductImages() {
    try {
      const result = await this.listFiles({ prefix: 'products/' });
      
      if (result.success) {
        return result.files.filter(file => {
          const pathname = file.pathname.toLowerCase();
          return pathname.endsWith('.jpg') || 
                 pathname.endsWith('.jpeg') || 
                 pathname.endsWith('.png') || 
                 pathname.endsWith('.webp') || 
                 pathname.endsWith('.gif');
        });
      }
      
      return [];
    } catch (error) {
      console.error('Error getting product images:', error);
      return [];
    }
  }

  /**
   * Upload product image with automatic naming
   * @param {File} file - The image file
   * @param {string} productName - Product name for filename
   * @returns {Promise<Object>} Upload result
   */
  async uploadProductImage(file, productName) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const sanitizedName = productName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const filename = `products/${sanitizedName}.${fileExtension}`;
    
    return await this.uploadFile(file, filename, {
      contentType: file.type
    });
  }

  /**
   * Migrate from Supabase to Vercel Blob
   * @param {Array} supabaseImages - Array of Supabase image objects
   * @returns {Promise<Array>} Migration results
   */
  async migrateFromSupabase(supabaseImages) {
    const migrationResults = [];
    
    for (const image of supabaseImages) {
      try {
        // Download image from Supabase
        const response = await fetch(image.url);
        const blob = await response.blob();
        
        // Create file object
        const file = new File([blob], image.name, { type: blob.type });
        
        // Upload to Vercel Blob
        const result = await this.uploadProductImage(file, image.name);
        
        migrationResults.push({
          originalUrl: image.url,
          newUrl: result.url,
          success: result.success,
          filename: image.name
        });
        
        console.log(`Migrated: ${image.name} -> ${result.success ? 'Success' : 'Failed'}`);
      } catch (error) {
        console.error(`Failed to migrate ${image.name}:`, error);
        migrationResults.push({
          originalUrl: image.url,
          success: false,
          error: error.message,
          filename: image.name
        });
      }
    }
    
    return migrationResults;
  }

  /**
   * CRUD OPERATIONS FOR VERCEL BLOB STORE
   * Comprehensive Create, Read, Update, Delete operations
   */

  /**
   * CREATE: Upload file to specific store path
   * @param {string} filename - The filename within the store
   * @param {File|Blob|string} content - The content to upload
   * @param {Object} options - Upload options
   * @returns {Promise<Object>} Upload result
   */
  async createFile(filename, content, options = {}) {
    try {
      const fullPath = `${this.storePath}/${filename}`;
      console.log(`📝 CREATE: Uploading ${fullPath} to Vercel Blob store ${this.storeId}`);

      const blob = await put(fullPath, content, {
        access: 'public',
        addRandomSuffix: false,
        ...options
      });

      console.log(`✅ CREATE SUCCESS: ${fullPath} uploaded successfully`);

      return {
        success: true,
        operation: 'CREATE',
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        filename: filename,
        fullPath: fullPath,
        storeId: this.storeId
      };
    } catch (error) {
      console.error(`❌ CREATE FAILED: Error uploading ${filename}:`, error);
      return {
        success: false,
        operation: 'CREATE',
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * READ: Get file information from store
   * @param {string} filename - The filename to read
   * @returns {Promise<Object>} File information
   */
  async readFile(filename) {
    try {
      const fullPath = `${this.storePath}/${filename}`;
      console.log(`📖 READ: Getting info for ${fullPath} from store ${this.storeId}`);

      const metadata = await head(fullPath);

      console.log(`✅ READ SUCCESS: ${fullPath} found`);

      return {
        success: true,
        operation: 'READ',
        url: metadata.url,
        downloadUrl: metadata.downloadUrl,
        pathname: metadata.pathname,
        size: metadata.size,
        uploadedAt: metadata.uploadedAt,
        filename: filename,
        fullPath: fullPath,
        storeId: this.storeId,
        metadata: metadata
      };
    } catch (error) {
      console.error(`❌ READ FAILED: Error reading ${filename}:`, error);
      return {
        success: false,
        operation: 'READ',
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * UPDATE: Replace existing file content
   * @param {string} filename - The filename to update
   * @param {File|Blob|string} newContent - The new content
   * @param {Object} options - Update options
   * @returns {Promise<Object>} Update result
   */
  async updateFile(filename, newContent, options = {}) {
    try {
      console.log(`🔄 UPDATE: Updating ${filename} in store ${this.storeId}`);

      // First check if file exists
      const existingFile = await this.readFile(filename);
      if (!existingFile.success) {
        return {
          success: false,
          operation: 'UPDATE',
          error: 'File not found for update',
          filename: filename
        };
      }

      // Delete the old file first
      const deleteResult = await this.deleteFileByUrl(existingFile.url);
      if (!deleteResult.success) {
        console.warn(`⚠️ Warning: Could not delete old file ${filename}, proceeding with upload`);
      }

      // Upload the new content
      const uploadResult = await this.createFile(filename, newContent, options);

      if (uploadResult.success) {
        console.log(`✅ UPDATE SUCCESS: ${filename} updated successfully`);
        return {
          ...uploadResult,
          operation: 'UPDATE',
          previousUrl: existingFile.url
        };
      } else {
        return {
          ...uploadResult,
          operation: 'UPDATE'
        };
      }
    } catch (error) {
      console.error(`❌ UPDATE FAILED: Error updating ${filename}:`, error);
      return {
        success: false,
        operation: 'UPDATE',
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * DELETE: Remove file from store by filename
   * @param {string} filename - The filename to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFileByName(filename) {
    try {
      console.log(`🗑️ DELETE: Removing ${filename} from store ${this.storeId}`);

      // First get the file info to get the URL
      const fileInfo = await this.readFile(filename);
      if (!fileInfo.success) {
        return {
          success: false,
          operation: 'DELETE',
          error: 'File not found for deletion',
          filename: filename
        };
      }

      // Delete using the URL
      await del(fileInfo.url);

      console.log(`✅ DELETE SUCCESS: ${filename} removed successfully`);

      return {
        success: true,
        operation: 'DELETE',
        message: 'File deleted successfully',
        deletedUrl: fileInfo.url,
        filename: filename,
        storeId: this.storeId
      };
    } catch (error) {
      console.error(`❌ DELETE FAILED: Error deleting ${filename}:`, error);
      return {
        success: false,
        operation: 'DELETE',
        error: error.message,
        filename: filename
      };
    }
  }

  /**
   * DELETE: Remove file from store by URL
   * @param {string} url - The blob URL to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFileByUrl(url) {
    try {
      console.log(`🗑️ DELETE: Removing file by URL from store ${this.storeId}`);

      await del(url);

      console.log(`✅ DELETE SUCCESS: File removed successfully`);

      return {
        success: true,
        operation: 'DELETE',
        message: 'File deleted successfully',
        deletedUrl: url,
        storeId: this.storeId
      };
    } catch (error) {
      console.error(`❌ DELETE FAILED: Error deleting file:`, error);
      return {
        success: false,
        operation: 'DELETE',
        error: error.message,
        url: url
      };
    }
  }

  /**
   * LIST: Get all files in the store
   * @param {Object} options - List options
   * @returns {Promise<Object>} List result with files
   */
  async listStoreFiles(options = {}) {
    try {
      console.log(`📋 LIST: Getting files from store ${this.storeId} path ${this.storePath}`);

      const { blobs } = await list({
        prefix: `${this.storePath}/`,
        limit: options.limit || 1000,
        cursor: options.cursor
      });

      const files = blobs.map(blob => ({
        filename: blob.pathname.replace(`${this.storePath}/`, ''),
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        storeId: this.storeId
      }));

      console.log(`✅ LIST SUCCESS: Found ${files.length} files in store`);

      return {
        success: true,
        operation: 'LIST',
        files: files,
        count: files.length,
        hasMore: blobs.length === (options.limit || 1000),
        storeId: this.storeId,
        storePath: this.storePath
      };
    } catch (error) {
      console.error(`❌ LIST FAILED: Error listing files from store:`, error);
      return {
        success: false,
        operation: 'LIST',
        error: error.message,
        files: []
      };
    }
  }

  /**
   * SEARCH: Find files by pattern in the store
   * @param {string} pattern - Search pattern (filename contains)
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchStoreFiles(pattern, options = {}) {
    try {
      console.log(`🔍 SEARCH: Looking for files matching "${pattern}" in store ${this.storeId}`);

      const listResult = await this.listStoreFiles(options);

      if (!listResult.success) {
        return listResult;
      }

      const matchingFiles = listResult.files.filter(file =>
        file.filename.toLowerCase().includes(pattern.toLowerCase())
      );

      console.log(`✅ SEARCH SUCCESS: Found ${matchingFiles.length} matching files`);

      return {
        success: true,
        operation: 'SEARCH',
        files: matchingFiles,
        count: matchingFiles.length,
        pattern: pattern,
        storeId: this.storeId
      };
    } catch (error) {
      console.error(`❌ SEARCH FAILED: Error searching files:`, error);
      return {
        success: false,
        operation: 'SEARCH',
        error: error.message,
        pattern: pattern,
        files: []
      };
    }
  }

  /**
   * BATCH DELETE: Remove multiple files from store
   * @param {Array} filenames - Array of filenames to delete
   * @returns {Promise<Object>} Batch deletion results
   */
  async batchDeleteFiles(filenames) {
    try {
      console.log(`🗑️ BATCH DELETE: Removing ${filenames.length} files from store ${this.storeId}`);

      const results = [];

      for (const filename of filenames) {
        const result = await this.deleteFileByName(filename);
        results.push({
          filename: filename,
          success: result.success,
          error: result.error
        });
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      console.log(`✅ BATCH DELETE COMPLETE: ${successCount} succeeded, ${failCount} failed`);

      return {
        success: failCount === 0,
        operation: 'BATCH_DELETE',
        results: results,
        successCount: successCount,
        failCount: failCount,
        storeId: this.storeId
      };
    } catch (error) {
      console.error(`❌ BATCH DELETE FAILED: Error in batch deletion:`, error);
      return {
        success: false,
        operation: 'BATCH_DELETE',
        error: error.message,
        filenames: filenames
      };
    }
  }

  /**
   * COPY: Copy file within the store (create duplicate with new name)
   * @param {string} sourceFilename - Source filename
   * @param {string} targetFilename - Target filename
   * @returns {Promise<Object>} Copy result
   */
  async copyFile(sourceFilename, targetFilename) {
    try {
      console.log(`📋 COPY: Copying ${sourceFilename} to ${targetFilename} in store ${this.storeId}`);

      // Read the source file
      const sourceFile = await this.readFile(sourceFilename);
      if (!sourceFile.success) {
        return {
          success: false,
          operation: 'COPY',
          error: 'Source file not found',
          sourceFilename: sourceFilename,
          targetFilename: targetFilename
        };
      }

      // Download the content
      const response = await fetch(sourceFile.downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to download source file content');
      }

      const content = await response.blob();

      // Upload to new location
      const uploadResult = await this.createFile(targetFilename, content);

      if (uploadResult.success) {
        console.log(`✅ COPY SUCCESS: ${sourceFilename} copied to ${targetFilename}`);
        return {
          ...uploadResult,
          operation: 'COPY',
          sourceFilename: sourceFilename,
          targetFilename: targetFilename,
          sourceUrl: sourceFile.url
        };
      } else {
        return {
          ...uploadResult,
          operation: 'COPY',
          sourceFilename: sourceFilename,
          targetFilename: targetFilename
        };
      }
    } catch (error) {
      console.error(`❌ COPY FAILED: Error copying file:`, error);
      return {
        success: false,
        operation: 'COPY',
        error: error.message,
        sourceFilename: sourceFilename,
        targetFilename: targetFilename
      };
    }
  }

  /**
   * GET STORE INFO: Get information about the store
   * @returns {Promise<Object>} Store information
   */
  async getStoreInfo() {
    try {
      console.log(`ℹ️ Getting store info for ${this.storeId}`);

      const listResult = await this.listStoreFiles();

      if (!listResult.success) {
        return {
          success: false,
          error: 'Failed to get store information'
        };
      }

      const totalSize = listResult.files.reduce((sum, file) => sum + (file.size || 0), 0);
      const imageFiles = listResult.files.filter(file => {
        const filename = file.filename.toLowerCase();
        return filename.endsWith('.jpg') || filename.endsWith('.jpeg') ||
               filename.endsWith('.png') || filename.endsWith('.gif') ||
               filename.endsWith('.webp');
      });

      const fileTypes = {};
      listResult.files.forEach(file => {
        const ext = file.filename.split('.').pop()?.toLowerCase() || 'unknown';
        fileTypes[ext] = (fileTypes[ext] || 0) + 1;
      });

      return {
        success: true,
        operation: 'STORE_INFO',
        storeId: this.storeId,
        storePath: this.storePath,
        totalFiles: listResult.files.length,
        totalSize: totalSize,
        imageFiles: imageFiles.length,
        fileTypes: fileTypes,
        files: listResult.files
      };
    } catch (error) {
      console.error(`❌ STORE INFO FAILED: Error getting store info:`, error);
      return {
        success: false,
        operation: 'STORE_INFO',
        error: error.message
      };
    }
  }
}

// Create and export singleton instance
const vercelBlobService = new VercelBlobService();
export default vercelBlobService;
